import { DateObject, type FanShopProductVariant, getTextColorForBackground, type ThemeType } from '@capsicum/common';
import { Card, CardBuilder, ModalService, SsuHud, Timer } from '@capsicum/common-web';
import { BundleTicketIcon, ReverseAuctionIcon } from '@capsicum/common-web/icons';
import { useProductCard } from '@capsicum/features/fanshop/shared';
import { useNCWBalance } from '@capsicum/socios-core';
import { TokenParticipationAmount } from '@capsicum/socios-core-web';
import { memo, type PropsWithChildren, useCallback, useMemo } from 'react';
import styled, { useTheme } from 'styled-components';

import { BuyFanTokensDrawer } from '../drawers/buy-fan-tokens-drawer/BuyFanTokensDrawer';

export type ProductCardProps = PropsWithChildren<{
	key?: string;
	onClick?: () => void;
	testID: string;
	variant: FanShopProductVariant;
}>;

/**
 * ProductCard is specific to each individual product in the fan shop
 *
 * @param {ProductCardProps} props - The props of the container
 * @param {FanShopProductVariant} props.variant The required amount of fan token(s)
 * @param {string} props.key Unique key per product card
 * @param {string} props.testID the ID to enable e2e framework to grab this element
 * @returns {JSX.Element} The ProductCard
 */

const ProductCardComponent = ({ onClick, testID, variant }: ProductCardProps) => {
	const { colors } = useTheme() as ThemeType;
	const { getTokenBalanceBy, isPending: isPendingNCWallet } = useNCWBalance();

	const {
		balance,
		hasEnoughFanTokens,
		hasEnoughStock,
		isComingSoon,
		isPackageProduct,
		isPendingCWallet,
		isReverseAuction,
		isTicketType,
		packageProductPrice,
		partnerCurrencyId,
		partnerId,
		partnerWallet,
		preventBuyFanTokensModal,
		redeemableAt,
		ribbonColor,
		ribbonText,
		setIsComingSoon,
		texts,
		tokenRequiredAmount,
		variantBlurHash,
		variantImage,
		variantPrice,
		variantTitle
	} = useProductCard({ getTokenBalanceBy, variant });

	const isPendingCombinedWallets = !isPendingCWallet && !isPendingNCWallet;

	const onBuyFanTokensClick = useCallback(
		(e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
			e.preventDefault();
			e.stopPropagation();

			if (preventBuyFanTokensModal) return;

			ModalService.addModal({
				renderComponent: ({ close }) => (
					<BuyFanTokensDrawer
						amount={texts.minimumAmount}
						closeModal={close}
						description={texts.getFanTokensText}
						partnerCurrencyId={partnerCurrencyId}
						testID="product-buy-fan-tokens-drawer"
						title={texts.fanTokensNeededText}
						walletAmount={`${balance} ${partnerWallet?.ledger?.currency?.shortCode || ''}`}
					/>
				)
			});
		},
		[
			balance,
			partnerCurrencyId,
			partnerWallet?.ledger?.currency?.shortCode,
			preventBuyFanTokensModal,
			texts.fanTokensNeededText,
			texts.getFanTokensText,
			texts.minimumAmount
		]
	);

	const cardTitle = useMemo(() => {
		return variantTitle.length > 27 ? variantTitle.substring(0, 27) + '...' : variantTitle;
	}, [variantTitle]);

	const image = useMemo(
		() =>
			variantImage ? (
				<S.StyledCardImage
					alt={`${variant.label} image`}
					blurhash={variantBlurHash}
					imageHeight={186}
					optimized
					src={variantImage}
				/>
			) : (
				<S.StyledCardImagePlaceholder />
			),
		[variantImage, variant.label, variantBlurHash]
	);

	const ribbon = useMemo(
		() => isTicketType && ribbonColor && <S.Ribbon $backgroundColor={ribbonColor}>{ribbonText}</S.Ribbon>,
		[isTicketType, ribbonColor, ribbonText]
	);

	const timer = useMemo(() => {
		if (isComingSoon) {
			return (
				<Card.Overlay additionalText={texts.availableIn} variant="full">
					<Timer
						customFormat="digitalTimer"
						expirationDate={new DateObject(redeemableAt!)}
						size="condensed"
						testID={`fanshop-${variantTitle}-digitalTimer`}
						type="default"
						onElapsed={() => setIsComingSoon(false)}
					/>
				</Card.Overlay>
			);
		} else if (!redeemableAt && hasEnoughStock) {
			return (
				<Card.Overlay variant="full">
					<Timer
						customText={texts.comingSoon}
						expirationDate={undefined}
						size="condensed"
						testID={`fanshop-${variantTitle}-comingSoon`}
					/>
				</Card.Overlay>
			);
		} else {
			return null;
		}
	}, [
		isComingSoon,
		texts.availableIn,
		redeemableAt,
		variantTitle,
		setIsComingSoon,
		hasEnoughStock,
		texts.comingSoon
	]);

	const overlay = useMemo(
		() => !hasEnoughStock && !isComingSoon && <Card.Overlay text={texts.outOfStock} variant="full" />,
		[hasEnoughStock, isComingSoon, texts.outOfStock]
	);

	const tokenParticipationAmount = useMemo(
		() =>
			!!partnerId &&
			!hasEnoughFanTokens() &&
			isPendingCombinedWallets && (
				<S.StyledTokenParticipationAmount
					amount={tokenRequiredAmount}
					partnerId={partnerId}
					testID="productCard-tokensNeeded-text"
					onClick={onBuyFanTokensClick}
				/>
			),
		[hasEnoughFanTokens, isPendingCombinedWallets, onBuyFanTokensClick, partnerId, tokenRequiredAmount]
	);

	const ssuHud = useMemo(
		() => (
			<SsuHud size="small" testID="product-card-ssu-hud">
				{isPackageProduct ? packageProductPrice : variantPrice}
			</SsuHud>
		),
		[isPackageProduct, packageProductPrice, variantPrice]
	);

	const icons = useMemo(
		() => (
			<>
				{isReverseAuction && <ReverseAuctionIcon color={colors.icons.main.enabled} height={24} width={24} />}
				{isPackageProduct && <BundleTicketIcon color={colors.icons.main.enabled} height={24} width={24} />}
			</>
		),
		[colors.icons.main.enabled, isPackageProduct, isReverseAuction]
	);

	const buildCard = new CardBuilder()
		.setMediaContent(
			partnerWallet && !!partnerId && (
				<div>
					<S.Wrapper>
						{image}
						{timer}
						{overlay}
						{tokenParticipationAmount}
					</S.Wrapper>
					{ribbon}
				</div>
			),
			{ height: 186 }
		)
		.setHeader(icons, 'end')
		.setHeader(ssuHud, 'start')
		.setTitle(cardTitle)
		.setCardStyle({
			maxWidth: 176,
			width: '50%'
		})
		.setOnActivate(() => onClick?.())
		.setProps({ testID })
		.build();

	return buildCard;
};

export const ProductCard = memo(ProductCardComponent);

namespace S {
	export const Wrapper = styled.div`
		position: relative;
	`;

	export const StyledCardImage = styled(Card.Image)`
		height: 186px;
	`;

	export const StyledCardImagePlaceholder = styled.div`
		height: 186px;
	`;

	export const Ribbon = styled.div<{ $backgroundColor: string | undefined }>`
		background-color: ${({ $backgroundColor, theme }) =>
			$backgroundColor || theme.colors.backgrounds.brand.enabled};
		display: flex;
		align-items: center;
		justify-content: center;
		text-transform: capitalize;
		${({ theme }) => theme.fonts.text.text4}
		color: ${({ $backgroundColor, theme }) =>
			getTextColorForBackground($backgroundColor!, 190)
				? theme.colors.white
				: theme.colors.common.scrim.background};
		width: 100%;
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
		padding: ${({ theme }) => theme.spacings.xxxsmall};
		position: absolute;
		bottom: 0;
		left: 0;
		z-index: 2;
	`;

	export const StyledTokenParticipationAmount = styled(TokenParticipationAmount)`
		display: flex;
		flex-direction: row;
		align-items: center;
		justify-content: center;
		position: absolute;
		left: ${({ theme }) => theme.spacings.small};
		top: ${({ theme }) => theme.spacings.small};
		z-index: 2;
	`;
}
